import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import './Suscripcion.css';

const Suscripcion = () => {
    // Steps: 0=Plan, 1=Datos, 2=Benef, 3=Docs, 4=Final
    const [currentStep, setCurrentStep] = useState(0);
    const totalSteps = 5;

    // Modals
    const [modalRightsOpen, setModalRightsOpen] = useState(false);
    const [modalSignatureOpen, setModalSignatureOpen] = useState(false);
    const [modalMaxBenefOpen, setModalMaxBenefOpen] = useState(false);

    // Form Data
    const [paymentFrequency, setPaymentFrequency] = useState('mensual'); // mensual | anual
    const [plan, setPlan] = useState('basico_familiar'); // basico_familiar | extendido_familiar
    
    const [suscriptor, setSuscriptor] = useState({
        nombreCompleto: '',
        tipoDoc: '',
        noDocumento: '',
        estadoCivil: '',
        celular: '',
        email: '',
        contacto: [], // array of strings
    });

    // Beneficiarios
    const [beneficiarios, setBeneficiarios] = useState([]);
    const [newBenef, setNewBenef] = useState({
        nombre: '',
        documento: '', // tipo
        nroDocumento: '',
        parentesco: '',
        celular: ''
    });

    // Docs
    const [files, setFiles] = useState([]);

    // Signature
    const [signatureData, setSignatureData] = useState(null);
    const sigCanvas = useRef({});
    
    // Legal Consent
    const [legalConsent, setLegalConsent] = useState(false);

    // Errors
    const [errors, setErrors] = useState({});

    // Refs for scrolling or focus if needed
    const topRef = useRef(null);

    // --- Handlers ---

    const handleNext = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(prev => prev + 1);
            window.scrollTo(0, 0);
        }
    };

    const handlePrev = () => {
        setCurrentStep(prev => prev - 1);
        window.scrollTo(0, 0);
    };

    const validateStep = (step) => {
        const newErrors = {};
        let isValid = true;

        if (step === 1) { // Datos Personales
            if (!suscriptor.nombreCompleto) newErrors.nombreCompleto = true;
            if (!suscriptor.tipoDoc) newErrors.tipoDoc = true;
            if (!suscriptor.noDocumento) newErrors.noDocumento = true;
            if (!suscriptor.estadoCivil) newErrors.estadoCivil = true;
            if (!suscriptor.celular) newErrors.celular = true;
            if (!suscriptor.email) newErrors.email = true;
            if (suscriptor.contacto.length === 0) newErrors.contacto = true;

            if (Object.keys(newErrors).length > 0) isValid = false;
        }

        if (step === 2) { // Beneficiarios
            if (beneficiarios.length === 0) {
                newErrors.beneficiarios = true;
                isValid = false;
            }
        }

        if (step === 3) { // Documentos
            if (files.length === 0) {
                newErrors.documentos = true;
                isValid = false;
            }
        }

        if (step === 4) { // Finalización
            if (!legalConsent) newErrors.legalConsent = true;
            if (!signatureData) newErrors.signature = true;
            
            if (Object.keys(newErrors).length > 0) isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSuscriptorChange = (e) => {
        const { name, value } = e.target;
        setSuscriptor(prev => ({ ...prev, [name]: value }));
    };

    const handleContactoChange = (e) => {
        const { value, checked } = e.target;
        setSuscriptor(prev => {
            const current = prev.contacto;
            if (checked) return { ...prev, contacto: [...current, value] };
            return { ...prev, contacto: current.filter(c => c !== value) };
        });
        // Clear error if selected
        if (errors.contacto) setErrors(prev => ({...prev, contacto: false}));
    };

    const handleAddBeneficiario = () => {
        // Validate newBenef
        if (!newBenef.nombre || !newBenef.documento || !newBenef.nroDocumento || !newBenef.parentesco || !newBenef.celular) {
            setErrors(prev => ({ ...prev, newBenef: true }));
            return;
        }

        if (beneficiarios.length >= 5) { // Plan says max 5 in modal, code said 2. Stick to 5 per modal.
            setModalMaxBenefOpen(true);
            return;
        }

        setBeneficiarios(prev => [...prev, newBenef]);
        setNewBenef({
            nombre: '',
            documento: '',
            nroDocumento: '',
            parentesco: '',
            celular: ''
        });
        setErrors(prev => ({ ...prev, newBenef: false, beneficiarios: false }));
    };

    const removeBeneficiario = (index) => {
        setBeneficiarios(prev => prev.filter((_, i) => i !== index));
    };

    const handleFileDrop = (e) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer.files);
        addFiles(droppedFiles);
    };

    const handleFileSelect = (e) => {
        const selectedFiles = Array.from(e.target.files);
        addFiles(selectedFiles);
    };

    const addFiles = (newFiles) => {
        setFiles(prev => [...prev, ...newFiles]);
        if (errors.documentos) setErrors(prev => ({...prev, documentos: false}));
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const clearSignature = () => {
        sigCanvas.current.clear();
    };

    const saveSignature = () => {
        if (sigCanvas.current.isEmpty()) {
            return;
        }
        const dataUrl = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
        setSignatureData(dataUrl);
        setModalSignatureOpen(false);
        if (errors.signature) setErrors(prev => ({...prev, signature: false}));
    };

    const handleSubmit = async () => {
        if (!validateStep(currentStep)) return; 

        // Construct JSON Payload
        const payload = {
            suscriptor: {
                tipo_documento: suscriptor.tipoDoc.toUpperCase(),
                numero_documento: suscriptor.noDocumento,
                nombres: suscriptor.nombreCompleto.split(" ")[0] || suscriptor.nombreCompleto,
                apellidos: suscriptor.nombreCompleto.split(" ").slice(1).join(" ") || ".",
                estado_civil: suscriptor.estadoCivil.toUpperCase(),
                email: suscriptor.email,
                celular: suscriptor.celular,
                medio_contacto: suscriptor.contacto.join(",").toUpperCase(),
                estado: "ACTIVO",
                derechos_deberes: true,
                ruta_firma: "https://bucket/firmas/firma_placeholder.png" // Mocked as requested
            },
            beneficiarios: beneficiarios.map(b => ({
                tipo_documento: b.documento.toUpperCase(),
                numero_documento: b.nroDocumento,
                nombres: b.nombre.split(" ")[0],
                apellidos: b.nombre.split(" ").slice(1).join(" ") || ".",
                telefono: b.celular,
                parentezco: b.parentesco.toUpperCase(),
                estado: "ACTIVO"
            })),
            documentos: files.map(f => ({
                url_documento: `https://bucket/documentos/${f.name}`,
                estado: "ACTIVO"
            }))
        };

        console.log("Submitting Payload:", payload);
        
        try {
            // Uncomment to actually submit
            // const response = await fetch('https://localhost:7082/api/Familia/RegistrarSuscripcion', {
            //     method: 'POST',
            //     headers: {'Content-Type': 'application/json'},
            //     body: JSON.stringify(payload)
            // });
            // const data = await response.json();
            
            alert("Suscripción registrada correctamente (Simulado). Revisa la consola para ver el JSON.");
            
        } catch (error) {
            console.error("Error submitting", error);
            alert("Error al registrar suscripción");
        }
    };


    // --- Render Helpers ---

    const getPlanPrice = (type) => {
        if (type === 'basico_familiar') return paymentFrequency === 'mensual' ? '$51.276' : '$524.040';
        if (type === 'extendido_familiar') return paymentFrequency === 'mensual' ? '$87.377' : '$952.200';
    };

    return (
        <div className="suscripcion-wrapper" ref={topRef}>
            <div className="suscripcion-container">
                {/* Header */}
                <header className="form-header">
                    <div className="logo-area">
                        <i className="fas fa-heart-pulse logo-icon"></i>
                        <h1>GMF <span className="highlight">Salud</span></h1>
                    </div>
                    <div className={`progress-container step-${currentStep + 1}`}>
                        <div className="progress-bar-bg"></div>
                        <div className="progress-bar-fill" style={{ width: `${currentStep * 25}%` }}></div>
                        <div className="steps-indicator">
                            {[0, 1, 2, 3, 4].map(s => (
                                <div key={s} className={`step ${s === currentStep ? 'active' : ''} ${s < currentStep ? 'completed' : ''}`}>
                                    {s < currentStep ? <i className="fas fa-check"></i> : s + 1}
                                </div>
                            ))}
                        </div>
                    </div>
                </header>

                <form className="wizard-form" onSubmit={e => e.preventDefault()}>
                    
                    {/* Step 1: Plan Selection */}
                    {currentStep === 0 && (
                        <div className="form-step active">
                            <h2 className="step-title">Elige tu Plan</h2>
                            <p className="step-subtitle">Selecciona la cobertura que mejor se adapte a ti</p>
                            
                            <div className="frequency-toggle-container">
                                <span className={`toggle-label ${paymentFrequency === 'mensual' ? 'active' : ''}`} onClick={() => setPaymentFrequency('mensual')}>Mensual</span>
                                <label className="switch">
                                    <input type="checkbox" checked={paymentFrequency === 'anual'} onChange={(e) => setPaymentFrequency(e.target.checked ? 'anual' : 'mensual')} />
                                    <span className="slider round"></span>
                                </label>
                                <span className={`toggle-label ${paymentFrequency === 'anual' ? 'active' : ''}`} onClick={() => setPaymentFrequency('anual')}>
                                    Anual <span className="badge-save">Ahorra</span>
                                </span>
                            </div>

                            <div className="plans-container">
                                <label className="plan-card">
                                    <input type="radio" name="plan" value="basico_familiar" checked={plan === 'basico_familiar'} onChange={() => setPlan('basico_familiar')} />
                                    <div className="plan-content">
                                        <div className="plan-icon"><i className="far fa-star"></i></div>
                                        <h3>Plan Básico Familiar</h3>
                                        <div className="price-container">
                                            <p className="plan-price">{getPlanPrice('basico_familiar')} <span className="period">/{paymentFrequency === 'mensual' ? 'mes' : 'año'}</span></p>
                                        </div>
                                        <ul className="plan-features">
                                            <li><i className="fas fa-user-doctor"></i> 4 Consultas con Especialista</li>
                                            <li><i className="fas fa-stethoscope"></i> 2 Consultas con Medico General</li>
                                            <li><i className="fas fa-vial"></i> 1 Examen de Hematología</li>
                                            <li><i className="fas fa-heart-circle-check"></i> Descuentos y Atención Preferencial</li>
                                        </ul>
                                        <span className="select-btn">Seleccionar</span>
                                    </div>
                                </label>

                                <label className="plan-card featured">
                                    <div className="badge">Recomendado</div>
                                    <input type="radio" name="plan" value="extendido_familiar" checked={plan === 'extendido_familiar'} onChange={() => setPlan('extendido_familiar')} />
                                    <div className="plan-content">
                                        <div className="plan-icon"><i className="fas fa-crown"></i></div>
                                        <h3>Plan Extendido Familiar</h3>
                                        <div className="price-container">
                                            <p className="plan-price">{getPlanPrice('extendido_familiar')} <span className="period">/{paymentFrequency === 'mensual' ? 'mes' : 'año'}</span></p>
                                        </div>
                                        <ul className="plan-features">
                                            <li><i className="fas fa-user-doctor"></i> 7 Consultas con Especialista</li>
                                            <li><i className="fas fa-stethoscope"></i> 5 Consulta Médico General</li>
                                            <li><i className="fas fa-vial"></i> 1 Examen de Hematología</li>
                                            <li><i className="fas fa-heart-circle-check"></i> Descuentos y Atención Preferencial</li>
                                        </ul>
                                        <span className="select-btn">Seleccionar</span>
                                    </div>
                                </label>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Personal Info */}
                    {currentStep === 1 && (
                        <div className="form-step active">
                            <h2 className="step-title">Tus Datos</h2>
                            <p className="step-subtitle">Información del titular de la suscripción</p>

                            <div className="input-group">
                                <label className={`floating-label ${suscriptor.nombreCompleto ? 'active' : ''}`}>
                                    <i className="far fa-user"></i>
                                    <input type="text" name="nombreCompleto" value={suscriptor.nombreCompleto} onChange={handleSuscriptorChange} required />
                                    <span>Nombre Completo</span>
                                </label>
                                {errors.nombreCompleto && <small className="error-text">Campo requerido</small>}
                            </div>

                            <div className="row-group">
                                <div className="input-group half">
                                    <label className={`floating-label ${suscriptor.tipoDoc ? 'active' : ''}`}>
                                        <i className="far fa-id-card"></i>
                                        <select name="tipoDoc" value={suscriptor.tipoDoc} onChange={handleSuscriptorChange} required>
                                            <option value="" disabled hidden></option>
                                            <option value="cc">Cédula de Ciudadanía</option>
                                            <option value="ce">Cédula de Extranjería</option>
                                            <option value="ti">Tarjeta de Identidad</option>
                                            <option value="pasaporte">Pasaporte</option>
                                        </select>
                                        <span>Tipo de Doc.</span>
                                    </label>
                                    {errors.tipoDoc && <small className="error-text">Requerido</small>}
                                </div>
                                <div className="input-group half">
                                    <label className={`floating-label ${suscriptor.noDocumento ? 'active' : ''}`}>
                                        <i className="fas fa-hashtag"></i>
                                        <input type="number" name="noDocumento" value={suscriptor.noDocumento} onChange={handleSuscriptorChange} required />
                                        <span>No. Documento</span>
                                    </label>
                                    {errors.noDocumento && <small className="error-text">Requerido</small>}
                                </div>
                            </div>

                            <div className="input-group">
                                <label className={`floating-label ${suscriptor.estadoCivil ? 'active' : ''}`}>
                                    <i className="fas fa-street-view"></i>
                                    <select name="estadoCivil" value={suscriptor.estadoCivil} onChange={handleSuscriptorChange} required>
                                        <option value="" disabled hidden></option>
                                        <option value="soltero">Soltero/a</option>
                                        <option value="casado">Casado/a</option>
                                        <option value="union_libre">Unión Libre</option>
                                        <option value="divorciado">Divorciado/a</option>
                                        <option value="viudo">Viudo/a</option>
                                    </select>
                                    <span>Estado Civil</span>
                                </label>
                                {errors.estadoCivil && <small className="error-text">Requerido</small>}
                            </div>

                            <div className="input-group">
                                <label className={`floating-label ${suscriptor.celular ? 'active' : ''}`}>
                                    <i className="fas fa-mobile-screen"></i>
                                    <input type="tel" name="celular" value={suscriptor.celular} onChange={handleSuscriptorChange} required />
                                    <span>Celular Personal</span>
                                </label>
                                {errors.celular && <small className="error-text">Requerido</small>}
                            </div>

                            <div className="input-group">
                                <label className={`floating-label ${suscriptor.email ? 'active' : ''}`}>
                                    <i className="far fa-envelope"></i>
                                    <input type="email" name="email" value={suscriptor.email} onChange={handleSuscriptorChange} required />
                                    <span>Correo Electrónico</span>
                                </label>
                                {errors.email && <small className="error-text">Requerido</small>}
                            </div>

                            <div className={`input-group checkbox-group ${errors.contacto ? 'error' : ''}`}>
                                <p className="group-label">Medio preferido de contacto:</p>
                                <div className="checkbox-options">
                                    <label className="custom-checkbox">
                                        <input type="checkbox" name="contacto" value="llamada" checked={suscriptor.contacto.includes('llamada')} onChange={handleContactoChange} />
                                        <span className="checkmark"></span>
                                        <span className="label-text"><i className="fas fa-phone"></i> Llamada</span>
                                    </label>
                                    <label className="custom-checkbox">
                                        <input type="checkbox" name="contacto" value="whatsapp" checked={suscriptor.contacto.includes('whatsapp')} onChange={handleContactoChange} />
                                        <span className="checkmark"></span>
                                        <span className="label-text"><i className="fab fa-whatsapp"></i> WhatsApp</span>
                                    </label>
                                    <label className="custom-checkbox">
                                        <input type="checkbox" name="contacto" value="email" checked={suscriptor.contacto.includes('email')} onChange={handleContactoChange} />
                                        <span className="checkmark"></span>
                                        <span className="label-text"><i className="far fa-envelope"></i> Email</span>
                                    </label>
                                </div>
                                {errors.contacto && <small className="error-text">Selecciona al menos uno</small>}
                            </div>
                        </div>
                    )}

                    {/* Step 3: Beneficiarios */}
                    {currentStep === 2 && (
                        <div className="form-step active">
                            <h2 className="step-title">Beneficiarios</h2>
                            <p className="step-subtitle">Agrega hasta 5 beneficiarios</p>

                            <div className="input-group">
                                <label className={`floating-label ${newBenef.nombre ? 'active' : ''}`}>
                                    <i className="far fa-user"></i>
                                    <input type="text" value={newBenef.nombre} onChange={e => setNewBenef({...newBenef, nombre: e.target.value})} />
                                    <span>Nombre Completo</span>
                                </label>
                            </div>

                            <div className="row-group">
                                <div className="input-group half">
                                    <label className={`floating-label ${newBenef.documento ? 'active' : ''}`}>
                                        <i className="far fa-id-card"></i>
                                        <select value={newBenef.documento} onChange={e => setNewBenef({...newBenef, documento: e.target.value})}>
                                            <option value="" disabled hidden></option>
                                            <option value="cc">Cédula de Ciudadanía</option>
                                            <option value="ce">Cédula de Extranjería</option>
                                            <option value="ti">Tarjeta de Identidad</option>
                                            <option value="pasaporte">Pasaporte</option>
                                        </select>
                                        <span>Tipo de Doc.</span>
                                    </label>
                                </div>
                                <div className="input-group half">
                                    <label className={`floating-label ${newBenef.nroDocumento ? 'active' : ''}`}>
                                        <i className="fas fa-hashtag"></i>
                                        <input type="number" value={newBenef.nroDocumento} onChange={e => setNewBenef({...newBenef, nroDocumento: e.target.value})} />
                                        <span>No. Documento</span>
                                    </label>
                                </div>
                            </div>

                            <div className="row-group">
                                <div className="input-group half">
                                    <label className={`floating-label ${newBenef.parentesco ? 'active' : ''}`}>
                                        <i className="fas fa-users"></i>
                                        <input type="text" value={newBenef.parentesco} onChange={e => setNewBenef({...newBenef, parentesco: e.target.value})} />
                                        <span>Parentesco</span>
                                    </label>
                                </div>
                                <div className="input-group half">
                                    <label className={`floating-label ${newBenef.celular ? 'active' : ''}`}>
                                        <i className="fas fa-mobile-screen"></i>
                                        <input type="tel" value={newBenef.celular} onChange={e => setNewBenef({...newBenef, celular: e.target.value})} />
                                        <span>Celular</span>
                                    </label>
                                </div>
                            </div>

                            <button type="button" className="btn btn-secondary btn-block" onClick={handleAddBeneficiario}>
                                <i className="fas fa-plus"></i> Agregar Beneficiario
                            </button>

                            {errors.newBenef && <small className="error-text">Completa todos los campos del beneficiario</small>}

                            <div className="beneficiarios-list">
                                {beneficiarios.map((b, i) => (
                                    <div key={i} className="beneficiario-chip">
                                        <span>{b.nombre} ({b.parentesco})</span>
                                        <button type="button" onClick={() => removeBeneficiario(i)}><i className="fas fa-xmark"></i></button>
                                    </div>
                                ))}
                            </div>
                            {errors.beneficiarios && <small className="error-text">Debes agregar al menos un beneficiario</small>}
                        </div>
                    )}

                    {/* Step 4: Documents */}
                    {currentStep === 3 && (
                        <div className="form-step active">
                            <h2 className="step-title">Documentos</h2>
                            <p className="step-subtitle">Sube los documentos requeridos de forma segura</p>

                            <div className="docs-requirements">
                                <h3><i className="fas fa-list-check"></i> Requisitos de Documentación</h3>
                                <ul>
                                    <li><i className="fas fa-check"></i> <strong>Cotizante:</strong> Cédula de ciudadanía vigente.</li>
                                    <li><i className="fas fa-check"></i> <strong>Beneficiarios:</strong> Documento de identidad según la edad:</li>
                                    <li className="nested"><i className="fas fa-circle-dot"></i> Menores de <strong>7 años</strong>: Registro civil de nacimiento.</li>
                                    <li className="nested"><i className="fas fa-circle-dot"></i> Entre <strong>7 y 17 años</strong>: Tarjeta de identidad.</li>
                                    <li className="nested"><i className="fas fa-circle-dot"></i> <strong>Mayores de edad:</strong> Cédula de ciudadanía u otro documento legalmente válido.</li>
                                </ul>
                            </div>

                            <div className="upload-area" onDrop={handleFileDrop} onDragOver={e => {e.preventDefault(); e.currentTarget.classList.add('dragover')}} onDragLeave={e => e.currentTarget.classList.remove('dragover')} onClick={() => document.getElementById('fileInput').click()}>
                                <div className="upload-icon"><i className="fas fa-cloud-arrow-up"></i></div>
                                <h3>Arrastra tus archivos aquí</h3>
                                <p>o haz clic para seleccionar</p>
                                <input type="file" id="fileInput" multiple hidden onChange={handleFileSelect} />
                            </div>

                            <div className="file-list">
                                {files.map((file, i) => (
                                    <div key={i} className="file-item">
                                        <div className="file-info">
                                            <i className="far fa-file-pdf file-icon"></i>
                                            <span>{file.name}</span>
                                        </div>
                                        <button type="button" className="delete-file" onClick={() => removeFile(i)}><i className="fas fa-trash"></i></button>
                                    </div>
                                ))}
                            </div>
                            {errors.documentos && <small className="error-text">Debes cargar al menos un documento</small>}

                            <div className="docs-note">
                                <i className="fas fa-circle-info"></i>
                                <p>
                                    <strong>Nota importante:</strong><br/>
                                    En todos los casos de beneficiarios, se debe presentar un documento que acredite el
                                    <strong>parentesco</strong> (registro civil de nacimiento u otro documento válido).
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Step 5: Finalization */}
                    {currentStep === 4 && (
                        <div className="form-step active">
                            <h2 className="step-title">Finalización</h2>
                            <p className="step-subtitle">Confirma y firma para completar tu suscripción</p>

                            <div className={`legal-consent-container ${errors.legalConsent ? 'error' : ''}`}>
                                <label className="custom-checkbox legal-checkbox">
                                    <input type="checkbox" checked={legalConsent} onChange={e => setLegalConsent(e.target.checked)} />
                                    <span className="checkmark"></span>
                                    <span className="label-text">
                                        He leído mis <strong>deberes y derechos</strong>
                                    </span>
                                </label>
                                <button type="button" className="info-btn" onClick={() => setModalRightsOpen(true)} title="Ver Deberes y Derechos">
                                    <i className="fas fa-circle-info"></i>
                                </button>
                            </div>
                            {errors.legalConsent && <small className="error-text">Debes aceptar los derechos y deberes</small>}

                            <div className="signature-section">
                                <h3>Firma del Suscriptor Titular</h3>
                                <p className="legal-text">
                                    Con mi firma acepto pagar el valor total de la suscripción y confirmo la veracidad de los datos.
                                </p>

                                <div className={`signature-preview-area ${errors.signature ? 'error' : ''}`}>
                                    {signatureData ? (
                                        <img id="signatureImage" src={signatureData} alt="Firma del usuario" />
                                    ) : (
                                        <div className="empty-signature">
                                            <i className="fas fa-signature"></i>
                                            <p>Sin firma</p>
                                        </div>
                                    )}
                                </div>
                                {errors.signature && <small className="error-text">Debes firmar el documento para continuar</small>}

                                <button type="button" className="btn btn-secondary btn-block" onClick={() => setModalSignatureOpen(true)}>
                                    <i className="fas fa-pen-nib"></i> Firmar Documento
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Navigation Controls */}
                    <div className="form-controls">
                        <button type="button" className="btn btn-secondary" onClick={handlePrev} disabled={currentStep === 0}>
                            <i className="fas fa-arrow-left"></i> Atrás
                        </button>
                        
                        {currentStep < 4 ? (
                            <button type="button" className="btn btn-primary" onClick={handleNext}>
                                Siguiente <i className="fas fa-arrow-right"></i>
                            </button>
                        ) : (
                            <button type="button" className="btn btn-success" onClick={handleSubmit}>
                                Finalizar Suscripción <i className="fas fa-check"></i>
                            </button>
                        )}
                    </div>
                </form>
            </div>

            {/* Modals */}
            <div className={`modal-overlay ${modalRightsOpen ? 'active' : ''}`} onClick={(e) => e.target === e.currentTarget && setModalRightsOpen(false)}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h3>Deberes y Derechos</h3>
                        <button type="button" className="close-modal" onClick={() => setModalRightsOpen(false)}>&times;</button>
                    </div>
                    <div className="modal-body">
                        <h4>Derechos del Suscriptor</h4>
                        <ul>
                            <li>Recibir atención médica oportuna y de calidad.</li>
                            <li>Ser tratado con dignidad y respeto.</li>
                            <li>Confidencialidad de su historia clínica.</li>
                        </ul>
                        <h4>Deberes del Suscriptor</h4>
                        <ul>
                            <li>Sudo de información veraz sobre su estado de salud.</li>
                            <li>Cumplir con las citas agendadas o cancelar a tiempo.</li>
                            <li>Realizar los pagos de la suscripción de forma oportuna.</li>
                        </ul>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-primary close-modal-btn" onClick={() => setModalRightsOpen(false)}>Entendido</button>
                    </div>
                </div>
            </div>

            <div className={`modal-overlay ${modalMaxBenefOpen ? 'active' : ''}`} onClick={(e) => e.target === e.currentTarget && setModalMaxBenefOpen(false)}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h3>Límite alcanzado</h3>
                        <button type="button" className="close-modal" onClick={() => setModalMaxBenefOpen(false)}>&times;</button>
                    </div>
                    <div className="modal-body">
                        <p>Has alcanzado el máximo permitido de <strong>5 beneficiarios</strong>.</p>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-primary" onClick={() => setModalMaxBenefOpen(false)}>Entendido</button>
                    </div>
                </div>
            </div>

            <div className={`modal-overlay ${modalSignatureOpen ? 'active' : ''}`}>
                <div className="modal-content signature-modal-content">
                    <div className="modal-header">
                        <h3>Firmar Documento</h3>
                        <button type="button" className="close-modal" onClick={() => setModalSignatureOpen(false)}>&times;</button>
                    </div>
                    <div className="modal-body">
                        <div className="signature-wrapper">
                            <SignatureCanvas 
                                ref={sigCanvas} 
                                penColor="black"
                                canvasProps={{
                                    className: 'sigCanvas',
                                    style: { width: '100%', height: '100%' }
                                }}
                            />
                        </div>
                        <div className="signature-tools">
                            <button type="button" className="tool-btn" onClick={clearSignature}><i className="fas fa-eraser"></i> Borrar</button>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={() => setModalSignatureOpen(false)}>Cancelar</button>
                        <button type="button" className="btn btn-primary" onClick={saveSignature}>Guardar Firma</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Suscripcion;
